import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { MetricasDesempenhoApi } from "../api/client.js";
import { Table } from "../components/Table.jsx";
import { useToast } from "../components/ToastProvider.jsx";
import { Modal } from "../components/Modal.jsx";
import { useIsMobile } from "../hooks/useIsMobile.js";

export default function MetricasDesempenhoPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: resp, isLoading } = useQuery({
    queryKey: ["metricas-desempenho"],
    queryFn: () => MetricasDesempenhoApi.listar(),
  });
  const metricas = resp?.data || resp || [];
  const isMobile = useIsMobile();

  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 10;

  const totalPaginas = useMemo(() => {
    if (!Array.isArray(metricas) || metricas.length === 0) return 1;
    return Math.ceil(metricas.length / itensPorPagina);
  }, [metricas]);

  const metricasPaginadas = useMemo(() => {
    const inicio = (paginaAtual - 1) * itensPorPagina;
    const fim = inicio + itensPorPagina;
    return metricas.slice(inicio, fim);
  }, [metricas, paginaAtual]);

  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(totalPaginas);
  }, [paginaAtual, totalPaginas]);

  const [cargo, setCargo] = useState("");
  const [tipo, setTipo] = useState("M1");
  const [descricao, setDescricao] = useState("");
  const [modalEditar, setModalEditar] = useState({
    open: false,
    id: null,
    cargo: "",
    type: "M1",
    descricao: "",
  });
  const [modalDeletar, setModalDeletar] = useState({
    open: false,
    id: null,
    cargo: "",
    type: "",
  });

  const criar = useMutation({
    mutationFn: () =>
      MetricasDesempenhoApi.criar({
        cargo,
        type: tipo,
        descricao,
      }),
    onSuccess: () => {
      toast.success("Metrica de desempenho criada");
      setCargo("");
      setTipo("M1");
      setDescricao("");
      qc.invalidateQueries({ queryKey: ["metricas-desempenho"] });
      qc.invalidateQueries({ queryKey: ["metricas-desempenho", "todos"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.message || "Erro ao criar"),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, dados }) => MetricasDesempenhoApi.atualizar(id, dados),
    onSuccess: () => {
      toast.success("Metrica de desempenho atualizada");
      qc.invalidateQueries({ queryKey: ["metricas-desempenho"] });
      qc.invalidateQueries({ queryKey: ["metricas-desempenho", "todos"] });
      setModalEditar({
        open: false,
        id: null,
        cargo: "",
        type: "M1",
        descricao: "",
      });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.message || "Erro ao atualizar"),
  });

  const deletar = useMutation({
    mutationFn: (id) => MetricasDesempenhoApi.deletar(id),
    onSuccess: () => {
      toast.success("Metrica de desempenho excluida");
      qc.invalidateQueries({ queryKey: ["metricas-desempenho"] });
      qc.invalidateQueries({ queryKey: ["metricas-desempenho", "todos"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.message || "Erro ao excluir"),
  });

  const abrirEdicao = (item) => {
    setModalEditar({
      open: true,
      id: item.id,
      cargo: item.cargo || "",
      type: item.type || "M1",
      descricao: item.descricao || "",
    });
  };

  const abrirDelecao = (item) => {
    setModalDeletar({
      open: true,
      id: item.id,
      cargo: item.cargo || "",
      type: item.type || "",
    });
  };

  const existeDuplicado = (cargoAlvo, tipoAlvo, idIgnorar = null) => {
    const cargoNorm = String(cargoAlvo || "")
      .trim()
      .toUpperCase();
    const tipoNorm = String(tipoAlvo || "")
      .trim()
      .toUpperCase();
    return metricas.some((item) => {
      if (!item) return false;
      if (idIgnorar && String(item.id) === String(idIgnorar)) return false;
      const cargoItem = String(item.cargo || "").trim().toUpperCase();
      const tipoItem = String(item.type || "").trim().toUpperCase();
      return cargoItem === cargoNorm && tipoItem === tipoNorm;
    });
  };

  const confirmarEdicao = () => {
    const { id, cargo: cargoEditado, type, descricao: descEditada } =
      modalEditar;
    if (existeDuplicado(cargoEditado, type, id)) {
      toast.error("Ja existe metrica desse tipo para este cargo.");
      return;
    }
    atualizar.mutate({
      id,
      dados: {
        cargo: cargoEditado,
        type,
        descricao: descEditada,
      },
    });
  };

  const confirmarDelecao = () => {
    deletar.mutate(modalDeletar.id);
    setModalDeletar({ open: false, id: null, cargo: "", type: "" });
  };

  const columns = [
    { key: "cargo", header: "Cargo" },
    { key: "type", header: "Tipo" },
    { key: "descricao", header: "Descricao" },
    {
      key: "acoes",
      header: "Acoes",
      render: (_, row) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: ".3rem",
          }}
        >
          <button
            className="btn"
            onClick={() => abrirEdicao(row)}
            style={{
              fontSize: ".7rem",
              padding: ".2rem .5rem",
              background: "#007bff",
            }}
          >
            Editar
          </button>
          <button
            className="btn"
            onClick={() => abrirDelecao(row)}
            style={{
              fontSize: ".7rem",
              padding: ".2rem .5rem",
              background: "#dc3545",
            }}
          >
            Excluir
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <h2 className="page-title">Metricas de Desempenho</h2>
      {/* <div className="card" style={{ marginBottom: "1rem" }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (existeDuplicado(cargo, tipo)) {
              toast.error("Ja existe metrica desse tipo para este cargo.");
              return;
            }
            criar.mutate();
          }}
          className="flex gap-sm"
          style={{ flexWrap: "wrap" }}
        >
          <input
            placeholder="Cargo"
            value={cargo}
            onChange={(e) => setCargo(e.target.value)}
            required
            style={{ flex: "1 1 180px" }}
          />
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            style={{ flex: "0 1 120px" }}
          >
            {["M1", "M2", "M3", "M4"].map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <input
            placeholder="Descricao"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            required
            style={{ flex: "2 1 240px" }}
          />
          <button className="btn" disabled={criar.isPending}>
            Criar
          </button>
        </form>
      </div> */}
      <div className="card">
        {isLoading ? (
          <p>Carregando...</p>
        ) : isMobile ? (
          <div className="collab-cards">
            {metricasPaginadas.map((m) => (
              <div className="collab-card" key={m.id}>
                <div className="collab-card-header">
                  {m.cargo || "(sem cargo)"}
                </div>
                <table className="mini-table">
                  <tbody>
                    <tr>
                      <th>Tipo</th>
                      <td>{m.type}</td>
                    </tr>
                    <tr>
                      <th>Descricao</th>
                      <td>{m.descricao}</td>
                    </tr>
                    <tr>
                      <th>Acoes</th>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            gap: ".3rem",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            className="btn"
                            onClick={() => abrirEdicao(m)}
                            style={{
                              fontSize: ".7rem",
                              padding: ".2rem .5rem",
                              background: "#007bff",
                            }}
                          >
                            Editar
                          </button>
                          <button
                            className="btn"
                            onClick={() => abrirDelecao(m)}
                            style={{
                              fontSize: ".7rem",
                              padding: ".2rem .5rem",
                              background: "#dc3545",
                            }}
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
            {!metricasPaginadas.length && (
              <p style={{ fontSize: ".7rem" }}>Nenhum registro</p>
            )}
          </div>
        ) : (
          <Table columns={columns} data={metricasPaginadas} keyField="id" />
        )}
        {!isLoading && metricas.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: ".5rem",
              marginTop: ".75rem",
              flexWrap: "wrap",
            }}
          >
            <p className="muted" style={{ fontSize: ".7rem", margin: 0 }}>
              Exibindo {(paginaAtual - 1) * itensPorPagina + 1} a{" "}
              {Math.min(paginaAtual * itensPorPagina, metricas.length)} de{" "}
              {metricas.length}
            </p>
            <div
              style={{
                display: "flex",
                gap: ".4rem",
                flex: "1 1 auto",
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                className="btn"
                onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
                disabled={paginaAtual === 1}
                aria-label="Pagina anterior"
                title="Pagina anterior"
                style={{ fontSize: ".7rem", padding: ".25rem .5rem" }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M15 6L9 12L15 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                className="btn"
                onClick={() =>
                  setPaginaAtual((p) => Math.min(totalPaginas, p + 1))
                }
                disabled={paginaAtual >= totalPaginas}
                aria-label="Proxima pagina"
                title="Proxima pagina"
                style={{ fontSize: ".7rem", padding: ".25rem .5rem" }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M9 6L15 12L9 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
      <Modal
        open={modalEditar.open}
        title="Editar metrica de desempenho"
        onClose={() =>
          setModalEditar({
            open: false,
            id: null,
            cargo: "",
            type: "M1",
            descricao: "",
          })
        }
        actions={
          <>
            <button
              className="btn"
              onClick={() =>
                setModalEditar({
                  open: false,
                  id: null,
                  cargo: "",
                  type: "M1",
                  descricao: "",
                })
              }
              style={{ background: "#6c757d" }}
            >
              Cancelar
            </button>
            <button
              className="btn"
              onClick={confirmarEdicao}
              disabled={atualizar.isPending}
              style={{ background: "#007bff" }}
            >
              Salvar
            </button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: ".6rem" }}>
          <input
            placeholder="Cargo"
            value={modalEditar.cargo}
            onChange={(e) =>
              setModalEditar((s) => ({ ...s, cargo: e.target.value }))
            }
          />
          <select
            value={modalEditar.type}
            onChange={(e) =>
              setModalEditar((s) => ({ ...s, type: e.target.value }))
            }
          >
            {["M1", "M2", "M3", "M4"].map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <textarea
            placeholder="Descricao"
            value={modalEditar.descricao}
            onChange={(e) =>
              setModalEditar((s) => ({ ...s, descricao: e.target.value }))
            }
            style={{ minHeight: "100px" }}
          />
        </div>
      </Modal>
      <Modal
        open={modalDeletar.open}
        title="Confirmar exclusao"
        onClose={() => setModalDeletar({ open: false, id: null, cargo: "", type: "" })}
        actions={
          <>
            <button
              className="btn"
              onClick={() =>
                setModalDeletar({ open: false, id: null, cargo: "", type: "" })
              }
              style={{ background: "#6c757d" }}
            >
              Cancelar
            </button>
            <button
              className="btn"
              onClick={confirmarDelecao}
              style={{ background: "#dc3545" }}
            >
              Excluir
            </button>
          </>
        }
      >
        <p>
          Tem certeza que deseja excluir a metrica de desempenho para{" "}
          <strong>
            {modalDeletar.cargo} {modalDeletar.type && `(${modalDeletar.type})`}
          </strong>
          ?
        </p>
      </Modal>
    </div>
  );
}
